import { DownloadIcon } from '@chakra-ui/icons';
import {
  AbsoluteCenter,
  Box,
  Button,
  Flex,
  Heading,
  InputGroup,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { getAllCustomersApi } from 'api/adminApi';
import { getCustomerPipelineApi } from 'api/customerCockpitProfileApi';
import { getAccountingInvoiceApi, listAccountingInvoicesApi } from 'api/accountingInternalApi';
import { createColumnHelper } from '@tanstack/react-table';
import DataTable from 'components/DataTable';
import ContentBox from 'components/ContentBox';
import { Currency } from 'components/Number';
import Card from 'components/card/Card';
import SelectInput from 'components/inputs/SelectInput';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadInvoicePdf } from './calc/downloadInvoicePdf';
import { filterInvoices } from './calc/filterInvoices';
import { filterLiveCustomers } from './calc/liveCustomers';
import PaymentStatusBadge from './components/PaymentStatusBadge';

const columnHelper = createColumnHelper();
const ALL_CUSTOMERS_VALUE = '';

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState(ALL_CUSTOMERS_VALUE);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [customerList, pipelineReport, invoiceList] = await Promise.all([
          getAllCustomersApi(),
          getCustomerPipelineApi(),
          listAccountingInvoicesApi(),
        ]);
        setCustomers(
          filterLiveCustomers(customerList, pipelineReport)
            .map((customer) => ({
              label: `${customer.companyName} (${customer.externalId})`,
              value: customer.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
        setInvoices(invoiceList || []);
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to load invoices',
          status: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [toast]);

  const filteredInvoices = useMemo(
    () =>
      filterInvoices(invoices, {
        customerId: customerFilter || null,
        searchQuery,
      }),
    [customerFilter, invoices, searchQuery]
  );

  const handleDownloadPdf = async (invoice) => {
    setDownloadingInvoiceId(invoice.id);
    try {
      const fullInvoice = await getAccountingInvoiceApi({ invoiceId: invoice.id });
      await downloadInvoicePdf(fullInvoice);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to generate PDF',
        status: 'error',
      });
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('invoiceNumber', { header: 'Invoice #' }),
      columnHelper.accessor('companyName', { header: 'Customer' }),
      columnHelper.accessor('invoiceDate', {
        header: 'Invoice date',
        cell: (info) => moment(info.getValue()).format('MM/DD/YYYY'),
      }),
      columnHelper.accessor('dueDate', {
        header: 'Due date',
        cell: (info) =>
          info.getValue() ? moment(info.getValue()).format('MM/DD/YYYY') : '—',
      }),
      columnHelper.accessor('totalDue', {
        header: 'Total due',
        cell: (info) => <Currency value={info.getValue()} maximumFractionDigits={2} />,
      }),
      columnHelper.accessor('paymentStatus', {
        header: 'Payment status',
        cell: (info) => <PaymentStatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor('createdByName', {
        header: 'Created by',
        cell: (info) => info.getValue() || '—',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const invoice = info.row.original;
          const isDownloading = downloadingInvoiceId === invoice.id;
          return (
            <Flex gap={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/accounting-internal/${invoice.id}`)}
              >
                View
              </Button>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<DownloadIcon />}
                isLoading={isDownloading}
                onClick={() => handleDownloadPdf(invoice)}
              >
                PDF
              </Button>
            </Flex>
          );
        },
      }),
    ],
    [downloadingInvoiceId, navigate]
  );

  if (loading) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  return (
    <ContentBox>
      <Card>
        <Flex justify="space-between" align="center" mb={6} gap={4} wrap="wrap">
          <Heading variant="sectionHeader" borderColor="twelve.green.300">
            Invoices
          </Heading>
          <Flex gap={3}>
            <Button variant="outline" onClick={() => navigate('/accounting-internal/new')}>
              Create invoice
            </Button>
            <Button variant="brand" onClick={() => navigate('/accounting-internal/set-off/new')}>
              Create set-off
            </Button>
          </Flex>
        </Flex>

        <Flex gap={4} mb={6} wrap="wrap" align="center">
          <Box minW="280px" flex="1 1 280px">
            <SelectInput
              placeholder="All customers"
              options={[
                { label: 'All customers', value: ALL_CUSTOMERS_VALUE },
                ...customers,
              ]}
              value={customerFilter}
              onChange={(value) => setCustomerFilter(value ?? ALL_CUSTOMERS_VALUE)}
              enableSearch
              isClearable
            />
          </Box>
          <InputGroup flex="1 1 280px">
            <SearchBar
              h="44px"
              w="100%"
              placeholder="Search invoice # or customer"
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </InputGroup>
        </Flex>

        {invoices.length ? (
          filteredInvoices.length ? (
            <DataTable
              columns={columns}
              data={filteredInvoices}
              defaultSorting={[{ id: 'invoiceDate', desc: true }]}
            />
          ) : (
            <Box textAlign="center" py={10}>
              <Text color="secondaryGray.600">No invoices match your filters.</Text>
            </Box>
          )
        ) : (
          <Box textAlign="center" py={10}>
            <Text color="secondaryGray.600" mb={4}>
              No invoices yet.
            </Text>
            <Button variant="brand" onClick={() => navigate('/accounting-internal/new')}>
              Create invoice
            </Button>
          </Box>
        )}
      </Card>
    </ContentBox>
  );
};

export default InvoiceList;
